import { Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';

/**
 * Synchronizes the Angular Material dialog stack with browser history.
 *
 * Current behavior:
 * - Opening a dialog pushes one history entry.
 * - Browser back closes only the current top dialog.
 * - Escape behaves the same as browser back.
 * - Closing a dialog through its own UI consumes only that dialog history entry.
 * - Nested dialogs close one by one in reverse order.
 * - Blocked dialogs stay open and restore the history position with browser forward.
 * - Dialogs closed outside browser navigation consume their own history entry afterwards.
 */
@Injectable({
    providedIn: 'root',
})
export class DialogEscapeService {
    private initialized = false;
    private nextDialogId = 1;
    private historyDepth = 0;

    private dialogIds = new WeakMap<MatDialogRef<any>, number>();
    private trackedDialogIds = new Set<number>();
    private blockedDialogIds = new Set<number>();
    private dialogDepths = new Map<number, number>();

    /**
     * Tracks dialogs that are being closed by browser navigation so their
     * `afterClosed` handler does not trigger an extra `history.back()`.
     */
    private navigationClosingDialogIds = new Set<number>();
    /** Skips the next popstate triggered by a dialog closing through its own UI. */
    private skipNextPopState = false;

    constructor(
        private dialog: MatDialog,
        private router: Router
    ) {}

    /** Registers global listeners and starts tracking newly opened dialogs once. */
    init() {
        if (this.initialized || typeof window === 'undefined' || typeof document === 'undefined') {
            return;
        }

        this.initialized = true;

        this.dialog.afterOpened.subscribe(dialogRef => {
            this.trackDialog(dialogRef);
        });

        document.addEventListener('keydown', this.onKeyDown, true);
        window.addEventListener('popstate', this.onPopState);
    }

    /** Marks a dialog as blocked or unblocked for escape and browser navigation closing. */
    setCloseBlocked(dialogRef: MatDialogRef<any> | null | undefined, blocked: boolean) {
        if (!dialogRef) {
            return;
        }

        const dialogId = this.getDialogId(dialogRef);

        if (blocked) {
            this.blockedDialogIds.add(dialogId);
        } else {
            this.blockedDialogIds.delete(dialogId);
        }
    }

    /** Starts tracking a dialog, assigns it a history depth, and pushes a history entry. */
    private trackDialog(dialogRef: MatDialogRef<any>) {
        const dialogId = this.getDialogId(dialogRef);
        if (this.trackedDialogIds.has(dialogId)) {
            return;
        }

        // A dialog opened while a route navigation is still resolving (e.g. from a
        // canDeactivate guard) must not be tracked here: pushing a history entry and
        // later cleaning it up with history.back() fires a popstate that the Router
        // picks up as a new navigation, cancelling the one currently in progress.
        if (this.router.getCurrentNavigation()) {
            return;
        }

        this.trackedDialogIds.add(dialogId);
        this.historyDepth++;
        this.dialogDepths.set(dialogId, this.historyDepth);

        window.history.pushState(
            {
                ...(window.history.state && typeof window.history.state === 'object' ? window.history.state : {}),
                dialogNavigationDepth: this.historyDepth,
            },
            '',
            window.location.href
        );

        dialogRef.afterClosed().subscribe(() => {
            this.handleDialogClosed(dialogRef);
        });
    }

    /** Cleans up dialog bookkeeping and syncs browser history after the dialog closes. */
    private handleDialogClosed(dialogRef: MatDialogRef<any>) {
        const dialogId = this.getDialogId(dialogRef);
        const dialogDepth = this.dialogDepths.get(dialogId);

        this.trackedDialogIds.delete(dialogId);
        this.blockedDialogIds.delete(dialogId);
        this.dialogDepths.delete(dialogId);

        if (this.historyDepth > 0) {
            this.historyDepth--;
        }

        if (this.navigationClosingDialogIds.delete(dialogId)) {
            return;
        }

        // If the app navigated elsewhere while the dialog was closing (e.g. the dialog
        // triggered a router navigation right before closing itself), the history entry
        // this dialog pushed has already been replaced. Going back in that case would
        // undo the navigation instead of just dismissing the dialog, so skip it.
        const currentState = window.history.state;
        const stillOnDialogEntry =
            currentState && typeof currentState === 'object' && currentState.dialogNavigationDepth === dialogDepth;

        if (typeof window !== 'undefined' && stillOnDialogEntry) {
            this.skipNextPopState = true;
            window.history.back();
        }
    }

    /** Maps escape to the same history flow as pressing the browser back button. */
    private onKeyDown = (event: KeyboardEvent) => {
        if (event.key !== 'Escape' || !this.dialog.openDialogs.length) {
            return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        const topDialog = this.getTopDialog();
        if (!topDialog || this.isCloseBlocked(topDialog)) {
            return;
        }

        window.history.back();
    };

    /** Closes the current top dialog when browser history moves backward or forward. */
    private onPopState = () => {
        if (this.skipNextPopState) {
            this.skipNextPopState = false;
            return;
        }

        const topDialog = this.getTopDialog();
        if (!topDialog) {
            return;
        }

        if (this.isCloseBlocked(topDialog)) {
            window.history.forward();
            return;
        }

        this.navigationClosingDialogIds.add(this.getDialogId(topDialog));
        topDialog.close();
    };

    /** Returns the currently topmost open dialog, if any. */
    private getTopDialog(): MatDialogRef<any> | undefined {
        return this.dialog.openDialogs[this.dialog.openDialogs.length - 1];
    }

    /** Returns whether a dialog is currently blocked from navigation-based closing. */
    private isCloseBlocked(dialogRef: MatDialogRef<any>): boolean {
        return this.blockedDialogIds.has(this.getDialogId(dialogRef));
    }

    /** Returns a stable internal numeric id for a dialog reference. */
    private getDialogId(dialogRef: MatDialogRef<any>): number {
        let dialogId = this.dialogIds.get(dialogRef);

        if (!dialogId) {
            dialogId = this.nextDialogId++;
            this.dialogIds.set(dialogRef, dialogId);
        }

        return dialogId;
    }
}
